import { apiService } from './api'
import { Group,  GroupFormData, Transaction, PaginatedResponse } from '../types'

export class GroupService {
  async getGroups(): Promise<Group[]> {
    const response = await apiService.get<Group[]>('/groups')
    return response
  }

  async getGroup(id: number): Promise<Group> {
    const response = await apiService.get<Group>(`/groups/${id}`)
    return response
  }

  async createGroup(data: GroupFormData): Promise<Group> {
    const response = await apiService.post<Group>('/groups', data)
    return response
  }

  async updateGroup(id: number, data: Partial<GroupFormData>): Promise<Group> {
    const response = await apiService.put<Group>(`/groups/${id}`, data)
    return response
  }

  async deleteGroup(id: number): Promise<void> {
    return apiService.delete<void>(`/groups/${id}`)
  }

  async inviteToGroup(groupId: number): Promise<{ inviteCode: string }> {
    const response = await apiService.post<{ inviteCode: string }>(`/groups/${groupId}/invite`, {})
    return response
  }

  async acceptInvitation(inviteCode: string): Promise<Group> {
    const response = await apiService.post<Group>('/groups/accept-invitation', { invite_code: inviteCode })
    return response
  }

  async leaveGroup(groupId: number): Promise<void> {
    return apiService.post<void>(`/groups/${groupId}/leave`, {})
  }

  async removeMember(groupId: number, memberId: number): Promise<void> {
    return apiService.delete<void>(`/groups/${groupId}/members/${memberId}`)
  }

  async updateMemberRole(
    groupId: number,
    memberId: number,
    role: 'member' | 'admin'
  ): Promise<void> {
    return apiService.put<void>(`/groups/${groupId}/members/${memberId}/role`, { role })
  }

  async transferOwnership(groupId: number, newOwnerId: number): Promise<void> {
    return apiService.post<void>(`/groups/${groupId}/transfer-ownership`, { new_owner_id: newOwnerId })
  }

  async getGroupActivity(groupId: number, filters?: any): Promise<any> {
    const response = await apiService.get<any>(`/groups/${groupId}/activity`, filters)
    return response
  }

  async getGroupTransactions(
    groupId: number,
    page = 1,
    limit = 50
  ): Promise<PaginatedResponse<Transaction>> {
    const params = { page, limit }
    return apiService.get<PaginatedResponse<Transaction>>(`/groups/${groupId}/transactions`, params)
  }

  async createGroupTransaction(groupId: number, data: any): Promise<Transaction> {
    return apiService.post<Transaction>(`/groups/${groupId}/transactions`, data)
  }

  async getGroupSummary(groupId: number, dateFrom?: string, dateTo?: string): Promise<{
    totalIncome: number
    totalExpenses: number
    netAmount: number
    transactionCount: number
    memberContributions: Array<{
      userId: number
      userName: string
      totalContribution: number
      transactionCount: number
    }>
  }> {
    const params = { dateFrom, dateTo }
    return apiService.get(`/groups/${groupId}/transactions/summary`, params)
  }
}

export const groupService = new GroupService()